<script setup lang="ts">
import { computed, ref } from "vue";
import { BrandMark, SegmentedControl, Wordmark } from "@/design-system";
import { usePlan } from "@/composables/usePlan";
import RaceLegsScreen from "@/features/race/RaceLegsScreen.vue";
import FuelScreen from "@/features/fuel/FuelScreen.vue";
import PlanScreen from "@/features/plan/PlanScreen.vue";
import LegalNotice from "@/features/legal/LegalNotice.vue";
import InstallPrompt from "@/features/install/InstallPrompt.vue";

/** The three-step flow: Rennen & Etappen -> Verpflegung -> Plan. */
const plan = usePlan();
const step = ref(0);

// Dark mode is switched off on request (2026-08-01). The night theme and the
// useTheme composable both stay -- re-enabling is this one call plus the
// inline script in index.html.

const steps = computed(() =>
  plan.t.value.steps.map((label, i) => ({ label, done: i <= step.value })),
);

/** Leaving step 1 past its last leg starts step 2 back at the first leg. */
function onRaceNext() {
  step.value = 1;
  plan.selectLeg(0);
}

function onFuelNext() {
  if (plan.advance() === "next-step") {
    step.value = 2;
    plan.selectLeg(0);
  }
}

/** FR-3.8: "Eingaben ändern" returns to step 2, leg 1. */
function onPlanEdit() {
  step.value = 1;
  plan.selectLeg(0);
}

/** FR-2.10: back from the first leg of step 2 returns to step 1's *last* leg. */
function onFuelBack() {
  if (plan.retreat() === "prev-step") {
    step.value = 0;
    plan.selectLeg(plan.order.value.length - 1);
  }
}
</script>

<template>
  <div class="shell">
    <header class="shell__head">
      <span class="shell__brand">
        <BrandMark :size="26" />
        <Wordmark :size="20" />
      </span>
      <span class="shell__meta">
        <SegmentedControl
          :model-value="plan.lang.value"
          :options="[
            { value: 'de', label: 'DE' },
            { value: 'en', label: 'EN' },
          ]"
          data-testid="lang"
          @update:model-value="(v) => plan.setLang(v as 'de' | 'en')"
        />
      </span>
    </header>

    <nav
      class="shell__steps"
      :aria-label="plan.t.value.stepMeta"
    >
      <div
        v-for="(s, i) in steps"
        :key="s.label"
        class="shell__step"
      >
        <span
          class="shell__bar"
          :class="{ 'shell__bar--done': s.done }"
        />
        <span
          class="shell__step-label"
          :class="{ 'shell__step-label--done': s.done }"
        >{{ s.label }}</span>
        <span class="sr-only">{{ i + 1 }}</span>
      </div>
    </nav>

    <main class="shell__body">
      <RaceLegsScreen
        v-if="step === 0"
        :plan="plan"
        @next="onRaceNext"
      />
      <FuelScreen
        v-else-if="step === 1"
        :plan="plan"
        @next="onFuelNext"
        @back="onFuelBack"
      />
      <PlanScreen
        v-else
        :plan="plan"
        @edit="onPlanEdit"
      />
    </main>

    <InstallPrompt :t="plan.t.value" />

    <LegalNotice :lang="plan.lang.value" />
  </div>
</template>

<style scoped>
.shell {
  max-width: 430px;
  margin: 0 auto;
  /* dvh, not vh: vh ignores the mobile browser's collapsing toolbars and
     leaves a strip of dead space at the bottom. */
  min-height: 100dvh;
  padding:
    calc(var(--space-5) + var(--safe-top))
    calc(var(--gutter-screen) + var(--safe-right))
    /* The pinned action bar carries its own inset, but the install prompt and
       the legal footer sit after it and would land under the home indicator. */
    calc(var(--space-6) + var(--safe-bottom))
    calc(var(--gutter-screen) + var(--safe-left));
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
.shell__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
}
/* Lockup per HANDOVER-Icons-ClaudeCode.md: mark 26, wordmark 20, 10px gap,
   clay applied here so the mark inherits it through currentColor. */
.shell__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--clay-500);
}
.shell__steps {
  display: flex;
  gap: var(--space-4);
}
/* The step bars are orientation, not content -- they can be tighter than the
   cards without losing their job. */
.shell__step {
  gap: var(--space-2);
}
.shell__step {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.shell__bar {
  height: 5px;
  border-radius: var(--radius-pill);
  background: var(--surface-sunken);
}
.shell__bar--done {
  background: var(--clay-500);
}
.shell__step-label {
  font: var(--type-label);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--text-faint);
}
.shell__step-label--done {
  color: var(--text-body);
}
.shell__body {
  display: contents;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
